import ErrorMessage from '@components/Common/ErrorMessage';
import EmptyState from '@components/Common/EmptyState';
import { PageHero, Panel } from '@components/Common/Panel';
import PageStats from '@components/Common/PageStats';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import MintlyTable from '@components/Common/Table';
import FormField from '@components/Common/FormField';
import SelectFormField from '@components/Common/SelectFormField';
import { useToast } from '@components/Common/Toast/ToastProvider';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutlined';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchDripSequences, fetchRegistrySources, setRulePaused, type DripSequence } from './api';
import './index.scss';
import {
  buildRegistry,
  FAMILY_LABELS,
  formatRateFraction,
  INTERNAL_AUDIENCE,
  isRuleActive,
  whenItFires,
  withRuleStatus,
  type EmailFamily,
  type EmailRegistryRow,
} from './registry';

/* The table's columns, named once so the loading skeleton can draw the same number of
   them. A skeleton with its own column count is a skeleton that stops matching. */
const EMAIL_COLUMNS = ['name', 'family', 'audience', 'when', 'status', 'performance', 'rule'];

const ROWS_PER_PAGE = 25;

/** Legend and filter share this order so the two never drift apart. */
const STATUS_ORDER = ['Active', 'Disabled', 'No trigger', 'Never sent'] as const;

/** Status chip colours live in the stylesheet as token references, per the design guardrails. */
const STATUS_CLASS: Record<string, string> = {
  Active: 'is-active',
  Disabled: 'is-disabled',
  'No trigger': 'is-no-trigger',
  'Never sent': 'is-never-sent',
};

/**
 * Every email the platform can send, in one list.
 *
 * Replaces the split between the Templates tab (content, no trigger, no performance) and the
 * Automation Rules tab (trigger, no content) — neither of which could answer "is this email
 * healthy" without the other.
 */
const EmailList: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<EmailRegistryRow[]>([]);
  const [sequences, setSequences] = useState<DripSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [family, setFamily] = useState<EmailFamily | 'all'>('all');
  const [status, setStatus] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const { showToast } = useToast();
  /* Rates are window-scoped, so the window is a control rather than a footnote — the same place
     Send Log puts it. */
  const [periodDays, setPeriodDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [{ templates, rules, stats }, drip] = await Promise.all([
        fetchRegistrySources(periodDays),
        // Sequences supply the real day offsets. Best-effort: a drip row still renders its step
        // number without them, it just cannot name the day.
        fetchDripSequences().catch(() => ({ sequences: [], stats: {} })),
      ]);
      setRows(buildRegistry(templates, rules, stats));
      setSequences(drip.sequences ?? []);
    } catch {
      setError('Could not load the email registry. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [periodDays]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Pause or resume the rule behind a row without leaving the list. Every rule-backed email gets
   * the switch, whatever its family: int-1 and the ct-* announcements have no other one here.
   */
  const toggleRule = async (row: EmailRegistryRow) => {
    if (!row.rule?.id) return;
    const pausing = isRuleActive(row.rule);
    setSavingKey(row.templateKey);
    try {
      const updated = await setRulePaused(row.rule.id, pausing);
      setRows((prev) =>
        prev.map((r) => (r.templateKey === row.templateKey ? withRuleStatus(r, pausing, updated) : r)),
      );
      showToast(pausing ? 'Rule paused' : 'Rule resumed', 'success');
    } catch {
      showToast(pausing ? 'Could not pause the rule' : 'Could not resume the rule', 'error');
    } finally {
      setSavingKey(null);
    }
  };

  /** templateKey -> day offset, so a drip row can say "day 5" instead of just "step 3". */
  const offsetByKey = useMemo(() => {
    const m = new Map<string, number>();
    sequences.forEach((s) => s.steps.forEach((st) => m.set(st.templateKey, st.dayOffset)));
    return m;
  }, [sequences]);

  const counts = useMemo(
    () => ({
      all: rows.length,
      transactional: rows.filter((r) => r.family === 'transactional').length,
      automation: rows.filter((r) => r.family === 'automation').length,
      drip: rows.filter((r) => r.family === 'drip').length,
    }),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(
      (r) =>
        (family === 'all' || r.family === family) &&
        (status === 'all' || r.status === status) &&
        (!q || r.name.toLowerCase().includes(q) || r.templateKey.toLowerCase().includes(q)),
    );
  }, [rows, family, status, search]);

  const paged = useMemo(
    () => filtered.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE),
    [filtered, page],
  );

  return (
    <Box className="eo-list-page">
      <Box className="eo-page-body max-w-container">
        <PageHero
          kicker="Email registry"
          title="Every email the platform can send, with what fires it and how it performs."
          text="Transactional, automation and drip in one list — open a row for its content, its trigger and its recent sends."
          actions={
            <ToggleButtonGroup
              exclusive
              size="small"
              value={periodDays}
              onChange={(_, v) => { if (v) setPeriodDays(v); }}
            >
              {[7, 30, 90].map((d) => (
                <ToggleButton key={d} value={d}>{d}d</ToggleButton>
              ))}
            </ToggleButtonGroup>
          }
        />

      <Panel>
      <Box className="eo-list__filters">
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, md: 4 }}>
            <SelectFormField
              required={false}
              label="Family"
              name="family"
              options={[
                { value: 'all', label: `All (${counts.all})` },
                { value: 'transactional', label: `Transactional (${counts.transactional})` },
                { value: 'automation', label: `Automation (${counts.automation})` },
                { value: 'drip', label: `Drip (${counts.drip})` },
              ]}
              size="small"
              value={family}
              onChange={(_, v) => { setFamily(v as EmailFamily | 'all'); setPage(0); }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <SelectFormField
              required={false}
              label="Status"
              name="status"
              options={[
                { value: 'all', label: 'Any status' },
                ...STATUS_ORDER.map((st) => ({ value: st, label: st })),
              ]}
              size="small"
              value={status}
              onChange={(_, v) => { setStatus(String(v)); setPage(0); }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormField
              required={false}
              label="Search"
              name="search"
              placeholder="Name or template key"
              size="small"
              value={search}
              onChange={(_, v) => { setSearch(String(v)); setPage(0); }}
            />
          </Grid>
        </Grid>
        {(family !== 'all' || status !== 'all' || search) && (
          <Button
            className="eo-list__reset"
            size="small"
            variant="text"
            onClick={() => { setFamily('all'); setStatus('all'); setSearch(''); setPage(0); }}
          >
            Reset
          </Button>
        )}
      </Box>
      </Panel>

      {!loading && !error && rows.length > 0 && (
        <PageStats filtered={filtered.length} label="emails" total={rows.length} />
      )}

      {loading ? (
        <SkeletonLoader rows={12} columns={EMAIL_COLUMNS.length} />
      ) : error ? (
        <ErrorMessage message={error} />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No emails match your filters"
          body="Widen the date range or clear a filter to see more."
        />
      ) : (
        <MintlyTable
          pagination={{
            enabled: true,
            page,
            rowsPerPage: ROWS_PER_PAGE,
            totalNumberOfRows: filtered.length,
            handleChangePage: (_e: unknown, next: number) => setPage(next),
          }}
          rows={paged}
          selectors={EMAIL_COLUMNS}
          tableHeadRow={{
            name: <>Email</>,
            family: <>Family</>,
            audience: <>Audience</>,
            when: <>When it fires</>,
            status: <>Status</>,
            performance: <>Performance</>,
            rule: <>Rule</>,
          }}
          tableBodyRow={{
            name: (row: EmailRegistryRow) => (
              <Box className="eo-cell-name">
                <span className="eo-cell-name__label" title={row.name}>{row.name}</span>
                <span className="eo-cell-name__key" title={row.templateKey}>{row.templateKey}</span>
              </Box>
            ),
            family: (row: EmailRegistryRow) => (
              <Chip
                className={`eo-chip eo-family-${row.family}`}
                label={FAMILY_LABELS[row.family]}
                size="small"
              />
            ),
            audience: (row: EmailRegistryRow) => <>{row.audience}</>,
            when: (row: EmailRegistryRow) => (
              <>{whenItFires(row, offsetByKey.get(row.templateKey))}</>
            ),
            status: (row: EmailRegistryRow) => (
              <Chip
                className={`eo-chip ${STATUS_CLASS[row.status]}`}
                label={row.status}
                size="small"
              />
            ),
            performance: (row: EmailRegistryRow) =>
              // Internal sends carry no tracking pixel, so 0% open would read as a failure.
              row.audience === INTERNAL_AUDIENCE && row.sent > 0 ? (
                <>{`${row.sent} sent, not tracked`}</>
              ) : row.sent === 0 ? (
                <>—</>
              ) : (
                <>{`${formatRateFraction(row.openRate)} open · ${formatRateFraction(row.clickRate)} click`}</>
              ),
            rule: (row: EmailRegistryRow) => {
              if (!row.rule?.id) return <span className="eo-rule-cell">{row.rule ? row.rule.ruleId : ''}</span>;
              const active = isRuleActive(row.rule);
              const label = `${active ? 'Pause' : 'Resume'} rule ${row.rule.ruleId ?? ''}`.trim();
              return (
                <Box className="eo-rule-cell">
                  <span className="eo-mono-pill">{row.rule.ruleId}</span>
                  <Tooltip title={label}>
                    <span>
                      <IconButton
                        aria-label={label}
                        disabled={savingKey === row.templateKey}
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRule(row);
                        }}
                      >
                        {active ? (
                          <PauseCircleOutlineIcon fontSize="small" />
                        ) : (
                          <PlayCircleOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            },
          }}
          onRowClick={(row: EmailRegistryRow) =>
            navigate(`/email-campaigns/emails/${encodeURIComponent(row.templateKey)}`)
          }
        />
      )}

      </Box>
    </Box>
  );
};

export default EmailList;
